import {
  Component,
  ChangeDetectionStrategy,
  ViewChild,
  ElementRef,
  ChangeDetectorRef,
  Output,
  EventEmitter,
  inject,
  Input,
} from '@angular/core';
import { PolymorpheusContent } from '@taiga-ui/polymorpheus';
import { TuiDialogContext, TuiDialogService } from '@taiga-ui/core';
import { TranslateService } from '@ngx-translate/core';
import { NumeralPluralsPipe } from '@src/app/shared/pipes';
import { DocumentTypes, MenuItem } from '@src/models';
import { APP_CONFIG } from '@src/core';
import { AlertService } from '@src/core/services';
import { EnvService } from '@src/app/modules/env';

import { AttachmentItem, AttachmentItems, AttachmentItemTypes, AttachmentMenuIcons } from './types';

@Component({
  selector: 'chat-attachment',
  templateUrl: './attachment.component.html',
  styleUrls: ['./attachment.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default,
})
export class AttachmentComponent {
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly numeralPluralsPipe = inject(NumeralPluralsPipe);
  private readonly translateService = inject(TranslateService);
  private readonly dialogService = inject(TuiDialogService);
  private readonly alertService = inject(AlertService);
  private readonly env = inject(EnvService);

  @Input() messageText = '';
  @ViewChild('fileInput') fileInput?: ElementRef;
  @ViewChild('confirmAttachmentDialog') confirmAttachmentDialog?: PolymorpheusContent<TuiDialogContext>;
  @Output() attachmentConfirmed = new EventEmitter<AttachmentItems>();

  attachmentMenuOpened: boolean = false;
  attachmentMenuItems: MenuItem<number, AttachmentMenuIcons>[] = [
    {
      id: 1,
      title: this.translateService.instant('components.telegramAttachment.labels.photo'),
      icon: 'image',
      action: () => {
        this.currentAttachmentType = 'image';
        this.chooseAttachment([...APP_CONFIG.fileTypes.image]);
      },
    },
    {
      id: 2,
      title: this.translateService.instant('common.labels.file'),
      icon: 'file',
      action: () => {
        this.currentAttachmentType = 'file';
        this.chooseAttachment();
      },
    },
  ];

  currentAttachmentPreview?: AttachmentItem[];
  currentAttachmentType: AttachmentItemTypes = 'file';

  fileInputChange(event: Event) {
    const files: FileList | null = (event.target as HTMLInputElement).files;
    if (!files?.length || !this.fileInput) {
      return;
    }

    const attachments = Array.from(files).filter(file => {
      if (file.size > APP_CONFIG.fileSizeMax.image) {
        this.alertService.warning(`Файл ${file.name} превышает объём 10 МБ, поэтому он исключен из вложений`, {
          autoClose: 5000,
        });
        return false;
      }

      return true;
    });

    if (this.fileInput?.nativeElement) {
      this.fileInput.nativeElement.value = null;
    }

    if (!attachments.length) {
      return;
    }

    const currentAttachments = attachments.filter((file, index) => {
      if (index >= 10) {
        this.alertService.warning(
          `Можно отправлять не более 10 файлов одновременно, поэтому файл ${file.name} исключен из вложений`,
          {
            autoClose: 5000,
          },
        );
        return false;
      }

      return true;
    });

    if (this.confirmAttachmentDialog && currentAttachments.length) {
      this.currentAttachmentPreview = this.getAttachmentPreview(currentAttachments);

      this.dialogService
        .open(this.confirmAttachmentDialog, {
          label: this.numeralPluralsPipe.transform(this.currentAttachmentPreview.length, [
            this.translateService.instant('components.telegramAttachment.labels.attachOne'),
            this.translateService.instant('components.telegramAttachment.labels.attachFew'),
            this.translateService.instant('components.telegramAttachment.labels.attachMany'),
          ]),
          closeable: true,
        })
        .subscribe();
    }
  }

  onClickAttachmentMenuItem(menuItem: MenuItem): void {
    this.attachmentMenuOpened = false;
    menuItem.action?.();
  }

  onConfirmedAttachment(attachments: AttachmentItems) {
    attachments.type = this.currentAttachmentType;
    this.attachmentConfirmed.emit(attachments);
  }

  private chooseAttachment(documentTypes?: DocumentTypes[]) {
    const inputElement = this.fileInput?.nativeElement;
    if (!inputElement) {
      return;
    }

    inputElement.accept = documentTypes;
    inputElement.click();
  }

  private getAttachmentPreview(attachments: File[]): AttachmentItem[] {
    const attachmentPreview: AttachmentItem[] = [];
    attachments.map((attachment, index) => {
      const attachmentItem: AttachmentItem = {
        sortOrder: index,
        file: attachment,
        path: window.URL.createObjectURL(attachment),
        type: this.getAttachmentItemType(attachment),
      };

      attachmentPreview.push(attachmentItem);
      this.cdr.markForCheck();
    });

    return attachmentPreview;
  }

  private getAttachmentItemType(file: File): AttachmentItemTypes | undefined {
    const attachmentItemTypes: AttachmentItemTypes[] = [AttachmentItemTypes.Image];
    for (const type of attachmentItemTypes) {
      if (file.type.match(new RegExp(`\^${type}\/*`))) {
        return type;
      } else {
        return AttachmentItemTypes.File;
      }
    }

    return undefined;
  }
}
